import { Ref, ref } from "vue";
import { defaultApi } from "@/api";
import { IResponse } from "@/api/Types";
import { NotificationStatus, ReportModule } from "@/global";
import i18n from "@/i18n";
import { UploadReportResponse } from "@/global/interfaces";
import { logInfo, logError } from "@/loggers";
import { FEATURE_NAMES, METRIC_TYPE } from "@/loggers/types";
import { logEvent, logMeasurement } from "@/loggers/logger.ts";
import { DATE_FORMAT_NOTIFICATIONS, UPLOAD_INBOX_NOTIFICATION_URL } from "../Constant";
import { getFormattedDate } from "../utils/notificationDrawerUtils";
import { getStatusIcon, getTagAppearance, getBackgroundColor, getAction, getTag } from "../utils/notificationTagsUtils";
import { useNotificationDrawerStore } from "@/store/NotificationDrawer/NotificationDrawer";

const useUploadFile = () => {
  const data: Ref<UploadReportResponse[]> = ref([]);
  const notifications = ref<UploadReportResponse[]>([]);
  const error = ref(null);
  const loading = ref(false);
  const notificationDrawerStore = useNotificationDrawerStore();

  // Pure function to get localized text - will be called fresh each time
  const getLocalizedMessage = (message?: string): string => {
    return message || i18n.global.t("NOTIFICATION.GENERATE_REPORT_MESSAGE");
  };

  const getLocalizedFileName = (fileName?: string): string => {
    return fileName || i18n.global.t("EMISSIONS_CONSTANTS.PEER_BENCHMARKING.DETAILED_ANALYSIS.NA");
  };

  const mapNotificationData = (data: any[]): any[] => {
    return data.map((item: any) => ({
      status: NotificationStatus[item.status] || NotificationStatus.IN_PROGRESS,
      icon: "tray-arrow-up",
      message: getLocalizedMessage(item.message),
      file: getLocalizedFileName(item.fileName),
      statusIcon: getStatusIcon(item.status),
      productName: item.productName,
      createdAt: getFormattedDate(item.createdAt, DATE_FORMAT_NOTIFICATIONS, false),
      metaIcon: "module-2",
      jobId: item.jobId,
      isUploadAcknowledged: item.isUploadAcknowledged,
      tagAppearance: getTagAppearance(item.status),
      backgroundColor: getBackgroundColor(item.status),
      action: getAction(item.status, item.downloadUrl, ReportModule.UPLOAD),
      tag: getTag(item.status, item.expiryTime)
    }));
  };

  // Method to refresh notifications with current locale
  const refreshLocalizedNotifications = (): void => {
    if (data.value.length > 0) {
      notifications.value = mapNotificationData(data.value);
    }
  };

  const fetchData = async (): Promise<UploadReportResponse[]> => {
    loading.value = true;
    const apiStart = performance.now();
    try {
      const response: IResponse = await defaultApi.apiUnversioned.uploadReports.getAll(UPLOAD_INBOX_NOTIFICATION_URL);
      data.value = response.data as UploadReportResponse[];

      notifications.value = mapNotificationData(response.data);
      logInfo(FEATURE_NAMES.MVS_ASYNC_UPLOAD, "Get all Notifications" + JSON.stringify(notifications.value));
      return notifications.value;
    } catch (err) {
      logEvent(FEATURE_NAMES.MVS_ASYNC_UPLOAD, METRIC_TYPE.API_CALL_FAILURE, {
        api: UPLOAD_INBOX_NOTIFICATION_URL,
        statusCode: err?.response?.status,
        error: err instanceof Error ? err.message : String(err)
      });
      logError(
        FEATURE_NAMES.MVS_ASYNC_UPLOAD,
        "Error while fetching upload notifications: " + (err instanceof Error ? err.message : String(err))
      );
      error.value = err;
    } finally {
      logMeasurement(FEATURE_NAMES.MVS_ASYNC_UPLOAD, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
        api: UPLOAD_INBOX_NOTIFICATION_URL
      });
      loading.value = false;
    }
  };

  const acknowledgeReportUpload = async () => {
    loading.value = true;
    try {
      const acknowledgeUploadReportUrl = "acknowledge";
      await defaultApi.apiUnversioned.uploadReports.patchReport(acknowledgeUploadReportUrl);
      logInfo(FEATURE_NAMES.MVS_ASYNC_UPLOAD, "All Notifications are acknowledged");
    } catch (err) {
      logError(
        FEATURE_NAMES.MVS_ASYNC_UPLOAD,
        "Error while acknowledging notification: " + (err instanceof Error ? err.message : String(err))
      );
      error.value = err;
    } finally {
      loading.value = false;
    }
  };

  const deleteData = async (report: UploadReportResponse) => {
    loading.value = true;
    try {
      const jobIdToDelete = report?.jobId;
      if (jobIdToDelete) {
        const deleteUrl = `/${jobIdToDelete}`;
        await defaultApi.apiUnversioned.uploadReports.deleteReport(deleteUrl);
      }
      notificationDrawerStore.filterNotification(jobIdToDelete);

      logInfo(
        FEATURE_NAMES.MVS_ASYNC_UPLOAD,
        "Deleted Notification from list, Updated List = " + " Deleted jobId = " + jobIdToDelete
      );
    } catch (err) {
      logError(
        FEATURE_NAMES.MVS_ASYNC_UPLOAD,
        "Error while Deleting Notification from list" + (err instanceof Error ? err.message : String(err))
      );
      error.value = err;
    } finally {
      loading.value = false;
    }
  };

  return {
    data,
    error,
    loading,
    fetchData,
    notifications,
    refreshLocalizedNotifications,
    acknowledgeReportUpload,
    deleteData
  };
};
export default useUploadFile;
